import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "./SubscriptionForm.css";
import { FaCheckCircle } from "react-icons/fa";
import { Button, Container, Row, Col, Form } from "react-bootstrap";
import BaseUrl from '../../ApiServices/BaseUrl';
import { useNavigate } from "react-router-dom";
import AdminNavbar from "../../Pages/Navbar/AdminNavbar";

const SubscriptionForm = () => {
  const { id } = useParams(); // Get the ID from URL params
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedPlan, setSelectedPlan] = useState("");
  const [selectedDays, setSelectedDays] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("INR");
  const [price, setPrice] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Use useEffect to fetch plan data when component mounts or ID changes
  useEffect(() => {
    const fetchPlanData = async () => {
      if (id) {
        setLoading(true);
        try {
          const response = await fetch(`${BaseUrl}/api/subscriptionplans/${id}`);
          if (!response.ok) {
            throw new Error('Failed to fetch plan data');
          }
          const planData = await response.json();

          console.log("Fetched plan data:", planData); // Debug log

          // Map the fetched plan data to the state
          setSelectedPlan(planData.plan_name || "");
          setSelectedDays(planData.listing_duration_days || "");
          setSelectedCurrency(planData.currency || "INR");
          setPrice(planData.price || "");

          // Map all the option fields from fetched planData to selectedOptions
          setSelectedOptions({
            position: planData.job_position_priority || "",
            access: planData.access_type || "",
            message: planData.messaging_limit || "",
            whatsapp: planData.whatsapp_access || "",
            call: planData.direct_call_access || "",
            share: planData.share_option || "",
            contact: planData.contact_details || "",
            candidate: planData.candidate_contact_details === 1 ? "Yes" : "No",
            cvLimit: planData.view_limit || "",
            // Convert boolean/number to string for New CV Alert selection
            cvAlert: planData.alerts_for_new_cvs === 1 ? "Yes" : "No",
            // Convert boolean/number to string for subscription selection
            subscription: planData.activate_deactivate_option === 1 ? "activate" : "deactivate",
            // Add daily limit count
            dailyLimit: planData.limit_count ? planData.limit_count.toString() : ""
          });

          console.log("Selected options set:", { // Debug log
            position: planData.job_position_priority,
            access: planData.access_type,
            contact: planData.contact_details,
            candidate: planData.candidate_contact_details,
            cvAlert: planData.alerts_for_new_cvs,
            subscription: planData.activate_deactivate_option,
            dailyLimit: planData.limit_count
          });

        } catch (err) {
          console.error("Failed to fetch plan data:", err);
          alert("Failed to load plan data!");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchPlanData();
  }, [id]);

  const handleSelect = (section, value) => {
    setSelectedOptions({ ...selectedOptions, [section]: value });
  };

  const renderOption = (section, value, label) => (
    <div
      className={`option-box ${selectedOptions[section] === value ? "selected" : ""}`}
      onClick={() => handleSelect(section, value)}
    >
      <div className="radio-circle">
        {selectedOptions[section] === value && <FaCheckCircle className="check-icon" />}
      </div>
      <span className="option-label">{label}</span>
    </div>
  );

  // Prepare payload dynamically
  const getPayload = () => ({
    plan_name: selectedPlan,
    listing_duration_days: selectedDays,
    access_type: selectedOptions.access || "",
    job_position_priority: selectedOptions.position || "",
    messaging_limit: selectedOptions.message || "",
    whatsapp_access: selectedOptions.whatsapp || "",
    direct_call_access: selectedOptions.call || "",
    share_option: selectedOptions.share || "",
    view_limit: selectedOptions.cvLimit || "",
    // Convert New CV Alert selection to boolean 1 or 0
    alerts_for_new_cvs: selectedOptions.cvAlert === "Yes" ? 1 : 0,
    full_contact_details: selectedOptions.contact || "",
    contact_details: selectedOptions.contact || "",
    candidate_contact_details: selectedOptions.candidate === "Yes" ? 1 : 0,
    // Convert subscription selection to boolean 1 or 0
    activate_deactivate_option: selectedOptions.subscription === "activate" ? 1 : 0,
    // Add daily limit count - convert string to number
    limit_count: selectedOptions.dailyLimit || null,
    price: price || null,
    currency: selectedCurrency
  });

  // Dynamically handle POST or PUT
  const handleSubmit = async () => {
    const method = id ? "PUT" : "POST";
    const url = id
      ? `${BaseUrl}/api/subscriptionplans/${id}`
      : `${BaseUrl}/api/subscriptionplans`;

    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(getPayload())
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      console.log("Response:", data);
      alert(id ? "Updated successfully!" : "Created successfully!");

      // Navigate to /managesubscriptionplans after successful submit
      window.location.href = '/managesubscriptionplans';

    } catch (err) {
      console.error(err);
      alert("Something went wrong!");
    }
  };

  // Add a cancel handler
  const handleCancel = () => {
    window.history.back();
  };

  if (loading) {
    return <div className="text-center mt-5">Loading plan data...</div>;
  }

  return (
    <>
     <AdminNavbar />
    <Container fluid className="subscription-container" style={{marginTop:'80px'}}>
      <div className="subscription-card">
        {/* Subscription Plan */}
        <div className="subscription-section">
          <h6>Subscription Plan/ Days/ Price</h6>
          <Row className="align-items-center">
            <Col xs={12} md={3}>
              <Form.Group className="custom-dropdown-container">
                <Form.Select
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  className="custom-dropdown"
                >
                  <option value="">Select Plan</option>
                  <option value="Free">Free Plan</option>
                  <option value="Silver">Silver Plan</option>
                  <option value="Gold">Gold Plan</option>
                </Form.Select>
                <div className="dropdown-arrow">▼</div>
              </Form.Group>
            </Col>
            <Col xs={12} md={3}>
              <Form.Group className="custom-dropdown-container">
                <Form.Select
                  value={selectedDays}
                  onChange={(e) => setSelectedDays(e.target.value)}
                  className="custom-dropdown"
                >
                  <option value="">Select Days</option>
                  {/* <option value="4 Days">4 Days</option> */}
                  <option value="7 Days">7 Days</option>
                  <option value="15 Days">15 Days</option>
                  <option value="30 Days">1 Month</option>
                  <option value="2 Months">2 Months</option>
                  <option value="3 Months">3 Months</option>
                  <option value="6 Months">6 Months</option>
                  <option value="12 Months">12 Months</option>
                </Form.Select>
                <div className="dropdown-arrow">▼</div>
              </Form.Group>
            </Col>
            <Col xs={12} md={3}>
              <Form.Group className="custom-dropdown-container">
                <Form.Select
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                  className="custom-dropdown"
                >
                  <option value="AED">AED (UAE Dirham)</option>
                  <option value="SAR">SAR (Saudi Riyal)</option>
                  <option value="QAR">QAR (Qatari Riyal)</option>
                  <option value="OMR">OMR (Omani Riyal)</option>
                  <option value="INR">INR (Indian Rupee)</option>
                  <option value="USD">USD (US Dollar)</option>
                  <option value="KWD">KWD (Kuwaiti Dinar)</option>
                  <option value="BHD">BHD (Bahraini Dinar)</option>
                  <option value="EUR">EUR (Euro)</option>
                  <option value="GBP">GBP (British Pound)</option>
                  <option value="SGD">SGD (Singapoor)</option>
                </Form.Select>
                <div className="dropdown-arrow">▼</div>
              </Form.Group>
            </Col>
            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Control
                  type="number"
                  placeholder="Enter Price"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="price-input"
                />
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* Job Posting Position */}
        <div className="subscription-section">
          <h6>Job Posting Position</h6>
          <Row>
            <Col xs={12} md={4}>
              {renderOption("position", "Top", "Top")}
            </Col>
            <Col xs={12} md={4}>
              {renderOption("position", "2nd Top", "Second")}
            </Col>
            <Col xs={12} md={4}>
              {renderOption("position", "Bottom Position", "Bottom")}
            </Col>
          </Row>
        </div>

        {/* Access Category */}
        <div className="subscription-section">
          <h6>Access Category</h6>
          <Row>
            <Col xs={12} md={4}>
              {renderOption("access", "Basic Access", "Basic Access")}
            </Col>
            <Col xs={12} md={4}>
              {renderOption("access", "Standard Access", "Standard Access")}
            </Col>
            <Col xs={12} md={4}>
              {renderOption("access", "Full Access", "Full Access")}
            </Col>
          </Row>
        </div>

        {/* Message Chat */}
        <div className="subscription-section">
          <h6>Message (Employer–Employee Chat/ day)</h6>
          <Row>
            <Col xs={12} md={4}>
              {renderOption("message", "Limited", "Limited")}
            </Col>
            <Col xs={12} md={4}>
              {renderOption("message", "Semi-Limited", "Semi-Limited")}
            </Col>
            <Col xs={12} md={4}>
              {renderOption("message", "Unlimited", "Unlimited")}
            </Col>
          </Row>
        </div>

        {/* WhatsApp Message */}
        <div className="subscription-section">
          <h6>WhatsApp Message</h6>
          <Row>
            <Col xs={12} md={4}>
              {renderOption("whatsapp", "NO", "NO Chat")}
            </Col>
            <Col xs={12} md={4}>
              {renderOption("whatsapp", "Limited", "Limited")}
            </Col>
            <Col xs={12} md={4}>
              {renderOption("whatsapp", "Unlimited", "Unlimited")}
            </Col>
          </Row>
        </div>

        {/* Call Access */}
        <div className="subscription-section">
          <h6>Call Access</h6>
          <Row>
            <Col xs={12} md={4}>
              {renderOption("call", "NO", "Basic")}
            </Col>
            <Col xs={12} md={4}>
              {renderOption("call", "Limited", "Standard")}
            </Col>
            <Col xs={12} md={4}>
              {renderOption("call", "Unlimited", "Full")}
            </Col>
          </Row>
        </div>

        {/* Share Option */}
        <div className="subscription-section">
          <h6>Share Option</h6>
          <Row>
            <Col xs={12} md={4}>
              {renderOption("share", "NO", "No Share")}
            </Col>
            <Col xs={12} md={4}>
              {renderOption("share", "Limited", "Limited Sharing")}
            </Col>
            <Col xs={12} md={4}>
              {renderOption("share", "Unlimited", "Unlimited Sharing")}
            </Col>
          </Row>
        </div>

        {/* Contact Detail */}
        <div className="subscription-section">
          <h6>Contact Detail (Employer/ Employee)</h6>
          <Row>
            <Col xs={12} md={4}>
              {renderOption("contact", "Whatsapp Call", "WhatsApp Call")}
            </Col>
            <Col xs={12} md={4}>
              {renderOption("contact", "Direct Call", "Direct Call")}
            </Col>
            <Col xs={12} md={4}>
              {renderOption("contact", "Message/ Email", "Message/ Email")}
            </Col>
          </Row>
        </div>

        {/* Candidates Contact Detail */}
        <div className="subscription-section">
          <h6>Candidates Contact Details</h6>
          {/* <Row>
            <Col xs={12} md={4}>
              {renderOption("candidate", "Basic Acccess for contact", "Basic Access")}
            </Col>
            <Col xs={12} md={4}>
              {renderOption("candidate", "Limited Acccess for contact", "Standard Access")}
            </Col>
            <Col xs={12} md={4}>
              {renderOption("candidate", "Full Acccess for contact", "Full Access")}
            </Col>
          </Row> */}
           <Row>
            <Col xs={12} md={6}>
              {renderOption("candidate", "Yes", "Yes")}
            </Col>
            <Col xs={12} md={6}>
              {renderOption("candidate", "No", "NO")}
            </Col>
          </Row>
        </div>

        {/* View Limit CV */}
        <div className="subscription-section">
          <h6>View Limit CV</h6>
          <Row>
            <Col xs={12} md={4}>
              {renderOption("cvLimit", "Limited", "Limited")}
            </Col>
            <Col xs={12} md={4}>
              {renderOption("cvLimit", "Semi-Limited", "Semi-Limited")}
            </Col>
            <Col xs={12} md={4}>
              {renderOption("cvLimit", "Unlimited", "Unlimited")}
            </Col>
          </Row>
        </div>

        {/* Daily limit count for whatsapp and call */}
        <div className="subscription-section">
          <h6>Daily limit count for whatsapp and call</h6>
          <Row>
            <Col xs={12} md={3}>
              {renderOption("dailyLimit", "0", "0")}
            </Col>
            <Col xs={12} md={3}>
              {renderOption("dailyLimit", "5", "5")}
            </Col>
            <Col xs={12} md={3}>
              {renderOption("dailyLimit", "10", "10")}
            </Col>
            <Col xs={12} md={3}>
              {renderOption("dailyLimit", "Unlimited", "Unlimited")}
            </Col>
          </Row>
        </div>

        {/* New CV Alert */}
        <div className="subscription-section">
          <h6>New CV Alert (Employer / Employee)</h6>
          <Row>
            <Col xs={12} md={6}>
              {renderOption("cvAlert", "Yes", "Yes")}
            </Col>
            <Col xs={12} md={6}>
              {renderOption("cvAlert", "No", "NO")}
            </Col>
          </Row>
        </div>

        {/* Subscription Activate/ Deactivate Option */}
        <div className="subscription-section">
          <h6>Subscription Activate/ Deactivate Option</h6>
          <Row>
            <Col xs={12} md={6}>
              {renderOption("subscription", "activate", "Subscription Activate")}
            </Col>
            <Col xs={12} md={6}>
              {renderOption("subscription", "deactivate", "Subscription Deactivate")}
            </Col>
          </Row>
        </div>

        <div className="subscriptionform-group">
          <Row className="g-2 button-row">
            <Col xs={12} md={6}>
              <Button className="subscription-group-update action-btn cancel-btn w-100" onClick={handleCancel}>
                Cancel
              </Button>
            </Col>
            <Col xs={12} md={6}>
              <Button className="subscription-group-update action-btn update-btn w-100" onClick={handleSubmit}>
                {id ? "Update" : "Create"}
              </Button>
            </Col>
          </Row>
        </div>
      </div>
    </Container>
    </>
  );
};

export default SubscriptionForm;